import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import { useC360List, C360Table, ColumnChooser, ViewIcon, EditIcon, DeleteIcon } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { describeSchedule, RUN_STATUS_META } from './scheduledTaskShared';

// ZAMANLANMIŞ GÖREVLER — LİSTE (menü 9608).
//
// Görev tipi adları KODDA SABİT DEĞİLDİR: /TaskTypes ucundan gelir. Yeni bir görev tipi backend'e
// eklendiğinde bu dosyaya dokunulmaz — combobox, filtre seçenekleri ve kolon etiketi kendiliğinden
// güncellenir. Çerçevenin vaadi budur.
const API = '/api/scheduled-tasks';

const SEARCH_FIELDS = ['Name'];

const ACTIVE_OPTIONS = [
  { value: 'true', label: 'Aktif' },
  { value: 'false', label: 'Pasif' },
];

function formatDateTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('tr-TR');
}

export function ScheduledTasksPage() {
  usePageMeta('Zamanlanmış Görevler', 'Periyodik çalışan görevlerin tanımı ve takibi.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [taskTypes, setTaskTypes] = useState([]);
  const [running, setRunning] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  // Görev tipi kataloğu — kolon etiketleri ve tip filtresi bundan beslenir.
  useEffect(() => {
    fetch(`${API}/TaskTypes`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => setTaskTypes(body?.data ?? body?.Data ?? []))
      .catch(() => {});
  }, []);

  const typeLabels = useMemo(
    () => Object.fromEntries(taskTypes.map((t) => [t.key, t.name])),
    [taskTypes],
  );

  const columnFilterMeta = useMemo(() => ({
    name: { type: 'text', odataField: 'Name' },
    taskType: {
      type: 'select',
      odataField: 'TaskType',
      options: taskTypes.map((t) => ({ value: t.key, label: t.name })),
    },
    // Bool alanda tırnak istenmez → valueFormat: 'raw' ($filter=IsActive eq true).
    isActive: { type: 'select', odataField: 'IsActive', valueFormat: 'raw', options: ACTIVE_OPTIONS },
  }), [taskTypes]);

  const list = useC360List({
    apiBase: API,
    columnFilterMeta,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'UpdatedDate',
    defaultSortDir: 'desc',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const openNew = () => navigate('/scheduled-tasks/new');
  const openDetail = (row) => { if (row) navigate(`/scheduled-tasks/${row.id}`, { state: { row } }); };
  // #282: kalem düzenleme moduna açar; göz/çift-tık izleme modunda açar.
  const openEditRow = (row) => { if (row) navigate(`/scheduled-tasks/${row.id}?edit=1`, { state: { row } }); };

  /**
   * "Şimdi Çalıştır" — görevi SENKRON koşar ve sonucunu döner.
   *
   * Zamanlama imlecine dokunmaz: elle çalıştırmak, gece 03:00 randevusunu iptal etmez.
   * Görev içindeki hata HTTP hatası olarak DEĞİL, sonuç nesnesinde status=FAILED olarak gelir —
   * bu yüzden res.ok'a bakmak yetmez, status'ü de kontrol ediyoruz.
   */
  const handleRunNow = useCallback(async (row) => {
    if (!row) return;
    setRunning(row.id);
    showToast('info', `“${row.name}” çalıştırılıyor…`);
    try {
      const res = await fetch(`${API}/RunNow`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ id: row.id }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Görev çalıştırılamadı');

      const result = body?.data ?? body?.Data ?? {};
      if (result.status === 'FAILED') showToast('error', `Görev hata verdi: ${result.message || 'bilinmeyen hata'}`);
      else showToast('success', result.message || 'Görev tamamlandı');

      fetchData();
    } catch (err) {
      showToast('error', err.message || 'Görev çalıştırılamadı');
    } finally {
      setRunning(null);
    }
  }, [fetchData]);

  /** Aktif/pasif. Pasif görev dispatcher tarafından hiç ele alınmaz. */
  const handleToggle = useCallback(async (row) => {
    try {
      const res = await fetch(`${API}/Toggle`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: row.id, isActive: !row.isActive }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Durum değiştirilemedi');
      showToast('success', row.isActive ? 'Görev pasifleştirildi' : 'Görev aktifleştirildi');
      fetchData();
    } catch (err) {
      showToast('error', err.message || 'Durum değiştirilemedi');
    }
  }, [fetchData]);

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API}/Delete`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Görev silinemedi');
      showToast('success', 'Görev silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (err) {
      showToast('error', err.message || 'Görev silinemedi');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: 'Görev Adı', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('taskType', {
      header: 'Görev Tipi', meta: { filterType: 'select' },
      cell: (info) => typeLabels[info.getValue()] || info.getValue() || '-',
    }),
    columnHelper.display({
      id: 'schedule', header: 'Zamanlama', enableColumnFilter: false,
      // Sunucuda tek bir "zamanlama metni" kolonu YOK — sıklık + saat + günlerden türetilir.
      cell: ({ row }) => describeSchedule(row.original),
    }),
    columnHelper.accessor('nextRunAt', {
      header: 'Sonraki Çalışma', enableColumnFilter: false,
      cell: (info) => (info.getValue() ? formatDateTime(info.getValue()) : '—'),
    }),
    columnHelper.accessor('lastRunAt', {
      header: 'Son Çalışma', enableColumnFilter: false,
      cell: ({ row }) => {
        const at = row.original.lastRunAt;
        if (!at) return <span style={{ color: 'var(--text-secondary)' }}>Hiç çalışmadı</span>;
        const meta = RUN_STATUS_META[row.original.lastRunStatus] ?? { label: '—', tone: 'neutral' };
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
            <span>{formatDateTime(at)}</span>
          </span>
        );
      },
    }),
    columnHelper.accessor('isActive', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => (
        <StatusBadge tone={info.getValue() ? 'ok' : 'neutral'}>
          {info.getValue() ? 'Aktif' : 'Pasif'}
        </StatusBadge>
      ),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && (
            <button
              type="button"
              className="c360-action-btn c360-action-btn--icon"
              title="Şimdi Çalıştır"
              aria-label="Şimdi Çalıştır"
              disabled={running === row.original.id}
              onClick={(e) => { e.stopPropagation(); handleRunNow(row.original); }}
            >
              {running === row.original.id ? '…' : '▶'}
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className="c360-action-btn c360-action-btn--icon"
              title={row.original.isActive ? 'Pasifleştir' : 'Aktifleştir'}
              aria-label={row.original.isActive ? 'Pasifleştir' : 'Aktifleştir'}
              onClick={(e) => { e.stopPropagation(); handleToggle(row.original); }}
            >
              {row.original.isActive ? '⏸' : '⏵'}
            </button>
          )}
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete, typeLabels, running, handleRunNow, handleToggle]);

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('UpdatedDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => row.id,
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'name', headerName: 'Görev Adı' },
        { headerName: 'Görev Tipi', valueGetter: ({ row }) => typeLabels[row.taskType] || row.taskType || '' },
        { headerName: 'Zamanlama', valueGetter: ({ row }) => describeSchedule(row) },
        { headerName: 'Sonraki Çalışma', valueGetter: ({ row }) => formatDateTime(row.nextRunAt) },
        { headerName: 'Son Çalışma', valueGetter: ({ row }) => formatDateTime(row.lastRunAt) },
        { headerName: 'Son Sonuç', valueGetter: ({ row }) => RUN_STATUS_META[row.lastRunStatus]?.label || '' },
        { headerName: 'Durum', valueGetter: ({ row }) => (row.isActive ? 'Aktif' : 'Pasif') },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `zamanlanmis_gorevler_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Görev adı ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button
          type="button"
          style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          title="Excel'e Aktar"
          aria-label="Excel'e Aktar"
          onClick={handleExport}
        >
          <ExcelIcon size={16} />
        </button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Zamanlanmış Görevler"
        subtitle="Periyodik çalışan görevler. Pasif görevler çalışmaz; “Şimdi Çalıştır” zamanlamayı değiştirmez."
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Görev</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={columnFilterMeta}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Henüz zamanlanmış görev tanımlanmamış."
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Görevi Sil</h3>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>“{selected?.name}”</strong> görevi silinecek ve bir daha çalışmayacak.
                <br /><br />
                Görevin daha önce yaptığı değişiklikler (ör. Potansiyel yapılan müşteriler)
                <strong> geri alınmaz</strong> — silmeden önce görevi bir kez çalıştırıp geri dönüşleri
                işletmek isteyebilirsiniz.
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default ScheduledTasksPage;
